import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { onAuthStateChanged, User } from 'firebase/auth';
import { collection, query, where, limit, onSnapshot, updateDoc, doc, setDoc, deleteDoc } from 'firebase/firestore';
import { ref, listAll, deleteObject } from 'firebase/storage';
import { QRCodeSVG } from 'qrcode.react';
import { auth, db, storage } from './firebase';
import {
  Receipt, Copy, ChevronDown, ChevronUp, ExternalLink,
  QrCode, Pencil, Check, X, Plus, Trash2, AlertTriangle, CheckCircle2, Clock,
} from 'lucide-react';
import './GroupEventAdmin.css';

// ── Types ─────────────────────────────────────────────────────────────────────

interface Item {
  id: string;
  description: string;
  amount: number;
  isGroup: boolean;
}

interface Contributor {
  name: string;
  receiptImageUrl: string | null;
  items: Item[];
  submittedAt: number;
  paymentHandle?: string | null;
}

interface GroupEvent {
  id: string;
  name: string;
  description: string;
  venue: string;
  eventDate: number;
  adminId: string;
  eventCode: string;
  totalMembers: number;
  contributors: Contributor[];
  paymentHandle?: string;
  status?: 'open' | 'locked';
  lockedAt?: number;
}

interface MemberBalance {
  balance: number;
  status: 'pending' | 'member_reported' | 'confirmed' | 'paid_out' | 'received';
  paymentHandle?: string;
}

interface Settlement {
  balances: Record<string, MemberBalance>;
  perPerson: number;
  groupTotal: number;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmt = (n: number) => `$${n.toFixed(2)}`;
const uid = () => `item-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;

function cGroupTotal(c: Contributor) {
  return c.items.filter(i => i.isGroup).reduce((s, i) => s + i.amount, 0);
}
function cPersonalTotal(c: Contributor) {
  return c.items.filter(i => !i.isGroup).reduce((s, i) => s + i.amount, 0);
}
function formatDate(ms: number) {
  return new Date(ms).toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
  });
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function GroupEventAdmin() {
  const { eventCode } = useParams<{ eventCode: string }>();
  const navigate = useNavigate();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showLockConfirm, setShowLockConfirm] = useState(false);
  const [locking, setLocking] = useState(false);
  const [settlement, setSettlement] = useState<Settlement | null>(null);
  const [confirmingPaid, setConfirmingPaid] = useState<string | null>(null);

  const [user, setUser] = useState<User | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [event, setEvent] = useState<GroupEvent | null>(null);
  const [eventLoading, setEventLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // UI state
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [copied, setCopied] = useState(false);
  const [showQR, setShowQR] = useState(false);

  // Member count editing
  const [editingMembers, setEditingMembers] = useState(false);
  const [memberDraft, setMemberDraft] = useState(0);
  const [savingMembers, setSavingMembers] = useState(false);

  // Contributor item editing
  const [editingContributor, setEditingContributor] = useState<string | null>(null);
  const [draftItems, setDraftItems] = useState<Item[]>([]);
  const [savingContributor, setSavingContributor] = useState(false);

  // Auth
  useEffect(() => {
    return onAuthStateChanged(auth, u => { setUser(u); setAuthLoading(false); });
  }, []);

  // Load event
  useEffect(() => {
    if (!user || !eventCode) return;
    const q = query(
      collection(db, 'users', user.uid, 'events'),
      where('eventCode', '==', eventCode.toUpperCase()),
      limit(1)
    );
    return onSnapshot(q, snap => {
      if (snap.empty) { setNotFound(true); setEventLoading(false); return; }
      setEvent(snap.docs[0].data() as GroupEvent);
      setEventLoading(false);
    }, () => { setNotFound(true); setEventLoading(false); });
  }, [user, eventCode]);

  // Subscribe to live settlement for payment status
  useEffect(() => {
    if (!event) return;
    return onSnapshot(doc(db, 'eventSettlements', event.eventCode), snap => {
      if (snap.exists()) setSettlement(snap.data() as Settlement);
    });
  }, [event?.eventCode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync eventSettlements doc whenever event data changes
  useEffect(() => {
    if (!event) return;
    const totalGroup = event.contributors.reduce(
      (s, c) => s + c.items.filter(i => i.isGroup).reduce((sum, i) => sum + i.amount, 0), 0
    );
    const pp = event.totalMembers > 0 ? totalGroup / event.totalMembers : 0;
    const settlementRef = doc(db, 'eventSettlements', event.eventCode);

    const updates: Record<string, any> = {
      eventCode: event.eventCode,
      eventName: event.name,
      paymentHandle: event.paymentHandle || '',
      totalMembers: event.totalMembers,
      groupTotal: totalGroup,
      perPerson: pp,
      updatedAt: Date.now(),
    };
    for (const c of event.contributors) {
      const paid = c.items.filter(i => i.isGroup).reduce((sum, i) => sum + i.amount, 0);
      updates[`balances.${c.name}.balance`] = paid - pp;
      if (c.paymentHandle) updates[`balances.${c.name}.paymentHandle`] = c.paymentHandle;
    }

    // updateDoc preserves existing statuses; fall back to setDoc if doc doesn't exist yet
    updateDoc(settlementRef, updates).catch(async () => {
      const balances: Record<string, { balance: number; status: string; paymentHandle?: string }> = {};
      for (const c of event.contributors) {
        const paid = c.items.filter(i => i.isGroup).reduce((sum, i) => sum + i.amount, 0);
        balances[c.name] = {
          balance: paid - pp,
          status: 'pending',
          ...(c.paymentHandle ? { paymentHandle: c.paymentHandle } : {}),
        };
      }
      await setDoc(settlementRef, {
        eventCode: event.eventCode,
        eventName: event.name,
        paymentHandle: event.paymentHandle || '',
        totalMembers: event.totalMembers,
        groupTotal: totalGroup,
        perPerson: pp,
        balances,
        updatedAt: Date.now(),
      });
    });
  }, [event]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Handlers ─────────────────────────────────────────────────────────────

  async function handleLockEvent() {
    if (!event || !user) return;
    setLocking(true);
    const now = Date.now();
    try {
      // Lock the event doc
      const q = query(
        collection(db, 'users', user.uid, 'events'),
        where('eventCode', '==', event.eventCode),
        limit(1)
      );
      const snap = await new Promise<any>((res, rej) => {
        const unsub = onSnapshot(q, s => { unsub(); res(s); }, rej);
      });
      if (!snap.empty) {
        await updateDoc(snap.docs[0].ref, { status: 'locked', lockedAt: now });
      }
      // Also mark settlement as locked so SettlementHub knows
      await updateDoc(doc(db, 'eventSettlements', event.eventCode), {
        status: 'locked', lockedAt: now,
      }).catch(() => {});
    } catch (e) {
      console.error(e);
    }
    setLocking(false);
    setShowLockConfirm(false);
  }

  async function confirmMemberPaid(name: string) {
    if (!event) return;
    setConfirmingPaid(name);
    const bal = settlement?.balances?.[name];
    const newStatus = bal && bal.balance < -0.01 ? 'confirmed' : 'received';
    await updateDoc(doc(db, 'eventSettlements', event.eventCode), {
      [`balances.${name}.status`]: newStatus,
      updatedAt: Date.now(),
    }).catch(console.error);
    setConfirmingPaid(null);
  }

  async function handleDeleteEvent() {
    if (!event || !user) return;
    setDeleting(true);
    try {
      // Delete receipt images from Storage
      const storageRef = ref(storage, `group-receipts/${event.eventCode}`);
      try {
        const { items } = await listAll(storageRef);
        await Promise.all(items.map(item => deleteObject(item)));
      } catch {
        // Storage folder may not exist — continue
      }
      // Delete settlement doc
      await deleteDoc(doc(db, 'eventSettlements', event.eventCode)).catch(() => {});
      // Delete the event doc itself
      await deleteDoc(doc(db, 'users', user.uid, 'events', event.id));
      navigate('/');
    } catch (e) {
      console.error(e);
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  }

  function toggleExpand(name: string) {
    setExpanded(prev => {
      const s = new Set(prev);
      s.has(name) ? s.delete(name) : s.add(name);
      return s;
    });
  }

  function copyLink() {
    if (!event) return;
    navigator.clipboard.writeText(`https://xpensemate.com/event/${event.eventCode}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  // Member count
  function startEditMembers() {
    setMemberDraft(event!.totalMembers);
    setEditingMembers(true);
  }

  async function saveMemberCount() {
    if (!event || memberDraft < 1) return;
    setSavingMembers(true);
    try {
      await updateDoc(doc(db, 'users', user!.uid, 'events', event.id), {
        totalMembers: memberDraft,
      });
    } finally {
      setSavingMembers(false);
      setEditingMembers(false);
    }
  }

  // Contributor items
  function startEditContributor(c: Contributor) {
    setEditingContributor(c.name);
    setDraftItems(c.items.map(i => ({ ...i })));
    setExpanded(prev => { const s = new Set(prev); s.add(c.name); return s; });
  }

  function cancelEditContributor() {
    setEditingContributor(null);
    setDraftItems([]);
  }

  async function saveContributor() {
    if (!event || !editingContributor) return;
    setSavingContributor(true);
    const validItems = draftItems.filter(i => i.description.trim() && i.amount > 0);
    const updated = event.contributors.map(c =>
      c.name === editingContributor ? { ...c, items: validItems } : c
    );
    try {
      await updateDoc(doc(db, 'users', user!.uid, 'events', event.id), {
        contributors: updated,
      });
      setEditingContributor(null);
      setDraftItems([]);
    } finally {
      setSavingContributor(false);
    }
  }

  function updateDraftItem(id: string, field: 'description' | 'amount', value: string) {
    setDraftItems(prev => prev.map(item =>
      item.id === id
        ? { ...item, [field]: field === 'amount' ? parseFloat(value) || 0 : value }
        : item
    ));
  }

  function toggleDraftGroup(id: string) {
    setDraftItems(prev => prev.map(item =>
      item.id === id ? { ...item, isGroup: !item.isGroup } : item
    ));
  }

  function addDraftItem() {
    setDraftItems(prev => [...prev, { id: uid(), description: '', amount: 0, isGroup: true }]);
  }

  function removeDraftItem(id: string) {
    setDraftItems(prev => prev.filter(item => item.id !== id));
  }

  // ── Loading / error ───────────────────────────────────────────────────────

  if (authLoading || (user && eventLoading)) {
    return <div className="ga-page"><div className="ga-center"><div className="ga-spinner" /></div></div>;
  }

  if (!user) {
    return (
      <div className="ga-page">
        <div className="ga-center">
          <p className="ga-msg">Sign in to view your event.</p>
          <button className="ga-btn-ghost" onClick={() => navigate('/')}>← Sign in</button>
        </div>
      </div>
    );
  }

  if (notFound || !event) {
    return (
      <div className="ga-page">
        <div className="ga-center">
          <p className="ga-msg">Event not found or you don't have access.</p>
          <button className="ga-btn-ghost" onClick={() => navigate('/')}>← Back to events</button>
        </div>
      </div>
    );
  }

  // ── Computed ──────────────────────────────────────────────────────────────

  const totalGroupExpense = event.contributors.reduce((s, c) => s + cGroupTotal(c), 0);
  const perPerson = event.totalMembers > 0 ? totalGroupExpense / event.totalMembers : 0;

  const localBalances = event.contributors.map(c => ({
    name: c.name,
    paid: cGroupTotal(c),
    balance: cGroupTotal(c) - perPerson,
  }));
  const getsBack = localBalances.filter(s => s.balance > 0.01).sort((a, b) => b.balance - a.balance);
  const owesPool = localBalances.filter(s => s.balance < -0.01).sort((a, b) => a.balance - b.balance);

  // Non-submitters who registered via settlement hub (paid or reported payment)
  const submitterNames = new Set(event.contributors.map(c => c.name));
  const knownNonSubmitters: [string, MemberBalance][] = settlement
    ? Object.entries(settlement.balances).filter(([name, b]) =>
        !submitterNames.has(name) && ['confirmed', 'member_reported'].includes(b.status))
    : [];
  const trulyPending = Math.max(0, event.totalMembers - event.contributors.length - knownNonSubmitters.length);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="ga-page">
      <header className="ga-header">
        <button className="ga-back" onClick={() => navigate('/')}>← Events</button>
        <div className="ga-logo">
          <div className="ga-logo-icon"><Receipt size={18} color="white" strokeWidth={2} /></div>
          <span className="ga-logo-text">XpenseMate</span>
        </div>
      </header>

      <main className="ga-main">

        {/* Event hero */}
        <div className="ga-hero">
          <h1 className="ga-title">{event.name}</h1>
          <div className="ga-meta">
            {event.venue && <><span>{event.venue}</span><span className="ga-dot">·</span></>}
            <span>{formatDate(event.eventDate)}</span>
          </div>
          {event.description && <p className="ga-desc">{event.description}</p>}
        </div>

        {/* Share link */}
        <div className="ga-share-row">
          <div className="ga-share-box">
            <ExternalLink size={14} className="ga-share-icon" />
            <span className="ga-share-url">xpensemate.com/event/{event.eventCode}</span>
          </div>
          <button className="ga-copy-btn" onClick={copyLink}>
            <Copy size={14} /> {copied ? 'Copied!' : 'Copy link'}
          </button>
          <button className="ga-qr-btn" onClick={() => setShowQR(v => !v)}>
            <QrCode size={14} /> {showQR ? 'Hide QR' : 'QR Code'}
          </button>
        </div>

        {/* Locked banner */}
        {event.status === 'locked' ? (
          <div className="ga-locked-banner">
            <span className="ga-locked-icon">🔒</span>
            <div>
              <strong>Submissions closed</strong>
              <span className="ga-locked-sub"> · Balances are final. Share the settlement link below.</span>
            </div>
          </div>
        ) : (
          <div className="ga-open-banner">
            <span>🟢 Accepting receipts</span>
            <button className="ga-btn-lock" onClick={() => setShowLockConfirm(true)}>
              Close &amp; lock submissions
            </button>
          </div>
        )}

        {showQR && (
          <div className="ga-qr-panel">
            <QRCodeSVG
              value={event.status === 'locked'
                ? `https://xpensemate.com/event/${event.eventCode}/settle`
                : `https://xpensemate.com/event/${event.eventCode}`}
              size={180}
              bgColor="#1a1a1a"
              fgColor="#ffffff"
              level="M"
            />
            <p className="ga-qr-hint">
              {event.status === 'locked'
                ? 'Members scan to see their balance & pay'
                : 'Members scan to upload receipts'}
            </p>
          </div>
        )}

        <a
          href={event.status === 'locked'
            ? `/event/${event.eventCode}/settle`
            : `/event/${event.eventCode}/settle`}
          className={`ga-settlement-link${event.status === 'locked' ? ' primary' : ''}`}
          target="_blank"
          rel="noreferrer"
        >
          {event.status === 'locked' ? '💳 Share settlement link →' : 'View live settlement hub →'}
        </a>

        {/* Stats */}
        <div className="ga-stats">
          {/* Submitted / member count — editable */}
          <div className="ga-stat-card">
            <div className="ga-stat-val">
              {event.contributors.length}
              {editingMembers ? (
                <span className="ga-member-edit-row">
                  <span className="ga-member-slash">/</span>
                  <input
                    className="ga-member-input"
                    type="number"
                    min="1"
                    value={memberDraft}
                    onChange={e => setMemberDraft(parseInt(e.target.value) || 1)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') saveMemberCount();
                      if (e.key === 'Escape') setEditingMembers(false);
                    }}
                    autoFocus
                  />
                  <button
                    className="ga-member-save"
                    onClick={saveMemberCount}
                    disabled={savingMembers}
                    title="Save"
                  >
                    <Check size={12} />
                  </button>
                  <button
                    className="ga-member-cancel"
                    onClick={() => setEditingMembers(false)}
                    title="Cancel"
                  >
                    <X size={12} />
                  </button>
                </span>
              ) : (
                <span className="ga-stat-denom">/{event.totalMembers}</span>
              )}
            </div>
            <div className="ga-stat-lbl">
              receipts
              {!editingMembers && (
                <button className="ga-edit-members-btn" onClick={startEditMembers} title="Edit member count">
                  <Pencil size={11} />
                </button>
              )}
            </div>
          </div>

          <div className="ga-stat-card accent">
            <div className="ga-stat-val">{fmt(perPerson)}</div>
            <div className="ga-stat-lbl">per person</div>
          </div>

          <div className="ga-stat-card">
            <div className="ga-stat-val">{fmt(totalGroupExpense)}</div>
            <div className="ga-stat-lbl">group total</div>
          </div>
        </div>

        {event.contributors.length === 0 ? (
          <div className="ga-no-submissions">
            <p>No submissions yet.</p>
            <p className="ga-hint">Share the link above with your group to get started.</p>
          </div>
        ) : (
          <>
            {/* Submissions */}
            <section className="ga-section">
              <h2 className="ga-section-title">Submissions</h2>
              <div className="ga-contributors">
                {event.contributors.map(c => {
                  const gt = cGroupTotal(c);
                  const pt = cPersonalTotal(c);
                  const isOpen = expanded.has(c.name);
                  const isEditing = editingContributor === c.name;
                  const bal = settlement?.balances?.[c.name];
                  const isPaid = bal?.status === 'confirmed' || bal?.status === 'received' || bal?.status === 'paid_out';
                  const reportedByMember = bal?.status === 'member_reported';
                  const isConfirming = confirmingPaid === c.name;

                  return (
                    <div key={c.name} className={`ga-contributor${isEditing ? ' editing' : ''}${isPaid ? ' paid' : ''}`}>
                      <div className="ga-contributor-header-row">
                        <button
                          className="ga-contributor-header"
                          onClick={() => !isEditing && toggleExpand(c.name)}
                        >
                          <div className="ga-contributor-info">
                            <div className="ga-contributor-name-row">
                              <span className="ga-contributor-name">{c.name}</span>
                              {bal && (
                                <span className={`ga-pay-status ${isPaid ? 'paid' : reportedByMember ? 'reported' : bal.balance > 0.01 ? 'gets' : 'owes'}`}>
                                  {isPaid
                                    ? <><CheckCircle2 size={11} /> {bal.balance > 0.01 ? 'Received' : 'Paid'}</>
                                    : reportedByMember
                                      ? <><Clock size={11} /> Reported</>
                                      : bal.balance > 0.01
                                        ? `Gets back ${fmt(bal.balance)}`
                                        : `Owes ${fmt(Math.abs(bal.balance))}`
                                  }
                                </span>
                              )}
                            </div>
                            <div className="ga-contributor-totals">
                              <span className="ga-total-g">Group {fmt(gt)}</span>
                              {pt > 0 && <span className="ga-total-p">Personal {fmt(pt)}</span>}
                              {c.items.length === 0 && <span className="ga-total-checkin">Check-in only</span>}
                            </div>
                          </div>
                          {!isEditing && (isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
                        </button>

                        {!isEditing && (
                          <div className="ga-contributor-actions">
                            {!isPaid && bal && (
                              <button
                                className={`ga-btn-confirm-paid${reportedByMember ? ' highlight' : ''}`}
                                onClick={() => confirmMemberPaid(c.name)}
                                disabled={isConfirming}
                                title={bal.balance > 0.01 ? 'Mark as received' : 'Confirm paid'}
                              >
                                {isConfirming ? '…' : <><Check size={13} /> {bal.balance > 0.01 ? 'Received' : 'Paid ✓'}</>}
                              </button>
                            )}
                            <button
                              className="ga-edit-contributor-btn"
                              onClick={() => startEditContributor(c)}
                              title="Edit items"
                            >
                              <Pencil size={14} /> Edit
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Expanded: view mode */}
                      {isOpen && !isEditing && (
                        <div className="ga-items">
                          {c.items.map(item => (
                            <div key={item.id} className="ga-item">
                              <span className="ga-item-desc">{item.description}</span>
                              <span className={`ga-item-tag ${item.isGroup ? 'group' : 'personal'}`}>
                                {item.isGroup ? 'Group' : 'Personal'}
                              </span>
                              <span className="ga-item-amt">{fmt(item.amount)}</span>
                            </div>
                          ))}
                          {c.receiptImageUrl && (
                            <a href={c.receiptImageUrl} target="_blank" rel="noreferrer" className="ga-receipt-link">
                              View receipt image ↗
                            </a>
                          )}
                        </div>
                      )}

                      {/* Edit mode */}
                      {isEditing && (
                        <div className="ga-edit-panel">
                          <div className="ga-edit-actions">
                            <button
                              className="ga-btn-save-contrib"
                              onClick={saveContributor}
                              disabled={savingContributor}
                            >
                              <Check size={14} /> {savingContributor ? 'Saving…' : 'Save changes'}
                            </button>
                            <button className="ga-btn-cancel-contrib" onClick={cancelEditContributor}>
                              <X size={14} /> Cancel
                            </button>
                          </div>

                          <div className="ga-edit-items">
                            {draftItems.map(item => (
                              <div key={item.id} className="ga-edit-item-row">
                                <input
                                  className="ga-edit-desc"
                                  type="text"
                                  placeholder="Item description"
                                  value={item.description}
                                  onChange={e => updateDraftItem(item.id, 'description', e.target.value)}
                                />
                                <div className="ga-edit-amount-wrap">
                                  <span className="ga-dollar">$</span>
                                  <input
                                    className="ga-edit-amount"
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    placeholder="0.00"
                                    value={item.amount || ''}
                                    onChange={e => updateDraftItem(item.id, 'amount', e.target.value)}
                                  />
                                </div>
                                <button
                                  className={`ga-edit-toggle ${item.isGroup ? 'group' : 'personal'}`}
                                  onClick={() => toggleDraftGroup(item.id)}
                                >
                                  {item.isGroup ? 'Group' : 'Personal'}
                                </button>
                                <button
                                  className="ga-edit-remove"
                                  onClick={() => removeDraftItem(item.id)}
                                >
                                  <Trash2 size={13} />
                                </button>
                              </div>
                            ))}
                          </div>

                          <button className="ga-btn-add-item" onClick={addDraftItem}>
                            <Plus size={13} /> Add item
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Settlement */}
            <section className="ga-section">
              <h2 className="ga-section-title">Settlement</h2>
              <div className="ga-settlement">
                {getsBack.length > 0 && (
                  <div className="ga-settlement-group">
                    <p className="ga-settlement-label green">Gets back from pool</p>
                    {getsBack.map(s => (
                      <div key={s.name} className="ga-settlement-row">
                        <span className="ga-settle-name">{s.name}</span>
                        <span className="ga-settle-paid">paid {fmt(s.paid)}</span>
                        <span className="ga-balance positive">+{fmt(s.balance)}</span>
                      </div>
                    ))}
                  </div>
                )}

                {owesPool.length > 0 && (
                  <div className="ga-settlement-group">
                    <p className="ga-settlement-label red">Owes pool</p>
                    {owesPool.map(s => (
                      <div key={s.name} className="ga-settlement-row">
                        <span className="ga-settle-name">{s.name}</span>
                        <span className="ga-settle-paid">paid {fmt(s.paid)}</span>
                        <span className="ga-balance negative">−{fmt(Math.abs(s.balance))}</span>
                      </div>
                    ))}
                  </div>
                )}

                {knownNonSubmitters.length > 0 && (
                  <div className="ga-settlement-group">
                    <p className="ga-settlement-label red">Owes pool (no receipt)</p>
                    {knownNonSubmitters.map(([name, b]) => (
                      <div key={name} className="ga-settlement-row">
                        <span className="ga-settle-name">{name}</span>
                        <span className="ga-settle-paid">paid $0.00</span>
                        <span className="ga-balance negative">−{fmt(perPerson)}</span>
                        <span className={`ga-pay-status ${b.status === 'confirmed' ? 'paid' : 'reported'}`}>
                          {b.status === 'confirmed'
                            ? <><CheckCircle2 size={11} /> Paid ✓</>
                            : <><Clock size={11} /> Reported</>}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {trulyPending > 0 && (
                  <div className="ga-pending-box">
                    <span className="ga-pending-count">{trulyPending}</span>
                    {' '}member{trulyPending > 1 ? 's' : ''} haven't submitted yet —
                    each owes <strong>{fmt(perPerson)}</strong> to the pool
                  </div>
                )}
              </div>
            </section>
          </>
        )}

        {/* Danger zone */}
        <div className="ga-danger-zone">
          <button className="ga-btn-delete" onClick={() => setShowDeleteConfirm(true)}>
            <Trash2 size={15} /> Delete event
          </button>
        </div>
      </main>

      {/* Lock confirmation modal */}
      {showLockConfirm && (
        <div className="ga-modal-overlay" onClick={() => !locking && setShowLockConfirm(false)}>
          <div className="ga-modal" onClick={e => e.stopPropagation()}>
            <div className="ga-modal-icon ga-modal-icon--lock">🔒</div>
            <h2 className="ga-modal-title">Close submissions?</h2>
            <p className="ga-modal-body">
              No new receipts will be accepted. Everyone's balance is finalised and you can
              share the <strong>settlement link</strong> so members see exactly what they owe or receive.
              You can still edit items and confirm payments after locking.
            </p>
            <div className="ga-modal-actions">
              <button
                className="ga-modal-cancel"
                onClick={() => setShowLockConfirm(false)}
                disabled={locking}
              >
                Not yet
              </button>
              <button
                className="ga-modal-confirm ga-modal-confirm--lock"
                onClick={handleLockEvent}
                disabled={locking}
              >
                {locking ? 'Locking…' : 'Yes, close submissions'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div className="ga-modal-overlay" onClick={() => !deleting && setShowDeleteConfirm(false)}>
          <div className="ga-modal" onClick={e => e.stopPropagation()}>
            <div className="ga-modal-icon"><AlertTriangle size={28} /></div>
            <h2 className="ga-modal-title">Delete this event?</h2>
            <p className="ga-modal-body">
              <strong>{event?.name}</strong> and all its submissions, receipts,
              and settlement data will be permanently deleted. This cannot be undone.
            </p>
            <div className="ga-modal-actions">
              <button
                className="ga-modal-cancel"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                className="ga-modal-confirm"
                onClick={handleDeleteEvent}
                disabled={deleting}
              >
                {deleting ? 'Deleting…' : 'Delete permanently'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
